import { useState } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Box,
  Button,
  ClickAwayListener,
  IconButton,
  MenuItem,
  MenuList,
  Paper,
  Popover,
} from '@mui/material';
import type { ButtonProps, SvgIconTypeMap } from '@mui/material';
import type { OverridableComponent } from '@mui/material/OverridableComponent';

const isItem = (item: Item | string): item is Item => (item as Item).label !== undefined;

// Falls back to a content-derived slug so every consumer gets a stable data-test even without
// passing `id` explicitly -- see the `id` field below for callers that need a slug independent
// of a label that can change at runtime (e.g. a dynamic label like "Install at Location" /
// "Change Location" for the same action).
const slugify = (label: string) => label.toLowerCase().replace(/[^a-z0-9]+/g, '-');

interface Item {
  label: string;
  disabled: boolean;
  /** Stable identifier for this item's data-test, independent of its (possibly dynamic) label. */
  id?: string;
}

export interface BasePopoverProps {
  items: (string | Item)[];
  dataTest?: string;
  buttonProps?: ButtonProps;
  buttonText?: string;
  icon?: OverridableComponent<SvgIconTypeMap<Record<string, any>, 'svg'>> & {
    muiName: string;
  };
  buttonType?: 'button' | 'iconButton';
  onClick?: (index: number) => void;
}

const BasePopover = ({
  items,
  onClick,
  icon: Icon = MoreVertIcon,
  dataTest = 'base-popover',
  buttonProps,
  buttonText = 'Actions',
  buttonType = 'button',
}: BasePopoverProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const isButton = buttonType === 'button';

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event?: MouseEvent | TouchEvent | Record<string, any>) => {
    event?.stopPropagation?.();
    setAnchorEl(null);
  };

  const handleClickItem =
    (index: number) => (event: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
      event.stopPropagation();
      onClick?.(index);
      handleClose();
    };

  return (
    <Box>
      {isButton ? (
        <Button
          variant="contained"
          {...buttonProps}
          endIcon={<ExpandMoreIcon />}
          onClick={handleClick}
          data-test={dataTest}
        >
          {buttonText}
        </Button>
      ) : (
        <IconButton onClick={handleClick} data-test={dataTest}>
          <Icon />
        </IconButton>
      )}

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        onClick={(ev) => {
          ev.stopPropagation();
        }} // Stop event propogation when menu item button is disabled
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        // MUI's enter transition leaves the backdrop absorbing clicks for its duration; a
        // click landing in that window (a fast/automated click right after opening) gets
        // swallowed instead of reaching the menu item. Instant transition removes the race.
        transitionDuration={0}
      >
        <Paper data-test="more-popover-content">
          <ClickAwayListener onClickAway={handleClose}>
            {/* autoFocusItem pulls focus onto the first item on mount, racing an immediate
                click on that same item (e.g. a fast/automated click right after the popover
                opens) -- only the first item is affected, which matches the failure only ever
                landing on the first menu entry. Not worth it for a "..." actions menu. */}
            <MenuList>
              {items.map((item, index) => {
                let label = item as string;
                let disabled = false;
                let id: string | undefined;
                if (isItem(item)) {
                  label = item.label;
                  disabled = item.disabled;
                  id = item.id;
                }

                return (
                  <MenuItem
                    key={`more-popover-item-${label}-${index}`}
                    onClick={handleClickItem(index)}
                    disabled={disabled}
                    data-test={`popover-item-${id ?? slugify(label)}`}
                  >
                    {label}
                  </MenuItem>
                );
              })}
            </MenuList>
          </ClickAwayListener>
        </Paper>
      </Popover>
    </Box>
  );
};

export default BasePopover;
